import { TourProvider } from '@reactour/tour';
import { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { Button } from '@/components/form/button';
import { beerSteps, ethanolSteps, getTourStep, wineSteps } from '@/features/demo/steps';
import { Theme } from '@/utils/types';

import { SignedInMethod, useAuth } from '../auth';
import { useSettings } from '../user-settings';
import { sendTeamsNotification, TeamsNotificationType } from './services/notification';

const backgroundColorMatrix = {
  [Theme.Beer]: '#008A00',
  [Theme.Wine]: '#EF426F',
  [Theme.Ethanol]: '#42c6ef',
};

const stepsMatrix = {
  [SignedInMethod.Beer]: beerSteps,
  [SignedInMethod.Wine]: wineSteps,
  [SignedInMethod.Ethanol]: ethanolSteps,
};

export function DemoProvider({ children }: { children: React.ReactElement }) {
  // custom hook to extract the current theme from Theme context
  const t: any = useTheme();
  const theme = t.current;

  const { user } = useAuth();
  const { setSettings, settings } = useSettings();

  const [currentStep, setCurrentStep] = useState(0);
  const [isFinishButtonLoading, setisFinishButtonLoading] = useState<boolean>(false);

  // color of logo will be changed based on the theme, default: wine color
  const backgroundColor = backgroundColorMatrix[theme as Theme];

  const steps = useMemo(
    () => (user ? stepsMatrix[user?.signedInMethod] : wineSteps),
    [user?.signedInMethod],
  );

  // get current step to adjust behavior of tour based on step
  const step = useMemo(() => steps[currentStep], [currentStep]);

  const handleTourClose = () => {
    setSettings({ ...settings, isDemoMode: false });
    window.location.reload();
  };

  const handleTourComplete = async () => {
    setisFinishButtonLoading(true);
    sendTeamsNotification({
      name: `${user?.first_name} ${user?.last_name}`,
      email: `${user?.email}`,
      type: TeamsNotificationType.Completed,
    }).finally(() => {
      setisFinishButtonLoading(false);
      handleTourClose();
    });
  };

  const stepWatcher = (step: number) => {
    if ((step as number) % 5 !== 0) {
      return;
    }
    const message = `User (${user?.signedInMethod}) completed step ${step as number} (${
      steps[step as number].uid
    }) out of ${steps.length}.`;
    sendTeamsNotification({
      message,
      email: `${user?.email}`,
      type: TeamsNotificationType.Progress,
      name: `${user?.first_name} ${user?.last_name}`,
    });
  };

  const routeToDemoFermentation = () => {
    window.history.replaceState(
      null,
      '',
      '/dashboard/beer/fermentations/tour/batch/64a2ac20dfb5a2bd84093516',
    );
    window.location.reload();
  };

  return (
    <TourProvider
      steps={steps}
      showBadge={false}
      showDots={false}
      scrollSmooth={true}
      onClickClose={handleTourClose}
      disableInteraction={step?.disableInteraction ? true : false}
      inViewThreshold={{ x: 100, y: 200 }}
      currentStep={currentStep}
      disableDotsNavigation={true}
      className="rounded-lg text-lg"
      setCurrentStep={(step) => {
        if (currentStep === steps.length - 1) {
          setCurrentStep(0);
        } else {
          setCurrentStep(step);
        }

        // trigger teams notification on each 5 step
        stepWatcher(step as number);
      }}
      styles={{
        badge: (base) => ({
          ...base,
          color: '#fff',
          backgroundColor,
        }),
        popover: (base) => ({
          ...base,
          '--reactour-accent': backgroundColor,
          padding: 40,
        }),
        highlightedArea: (base, rest) => ({
          ...base,
          ...rest,
          display: step.disableInteraction ? 'block' : 'none',
          fill: [
            0,
            getTourStep(steps, 'BWbcFaA'),
            getTourStep(steps, '3dIOlms'),
          ].includes(currentStep)
            ? 'rgb(0,0,0)'
            : 'transparent',
        }),
      }}
      prevButton={({ Button: PrevButton }) => {
        return <PrevButton kind="prev" hideArrow={step.disablePrevButton} />;
      }}
      showCloseButton={currentStep !== getTourStep(steps, '3dIOlms')}
      nextButton={({ Button: NextButton }) => {
        if (currentStep === getTourStep(steps, '3dIOlms'))
          return (
            <Button isLoading={isFinishButtonLoading} onClick={handleTourComplete}>
              Finish
            </Button>
          );

        if (currentStep === getTourStep(steps, 'OkjY32s'))
          return (
            <NextButton
              kind="next"
              hideArrow={step.disableNextButton}
              onClick={routeToDemoFermentation}
            />
          );

        if (currentStep === getTourStep(steps, 'tX6eJ0i'))
          return (
            <NextButton kind="next" hideArrow={step.disableNextButton} onClick={() => {}}>
              Click the button above 👆
            </NextButton>
          );

        return <NextButton kind="next" hideArrow={step.disableNextButton} />;
      }}
      onClickHighlighted={(e) => {
        e.stopPropagation();
      }}
      onClickMask={() => {}}
      keyboardHandler={(e, clickProps) => {
        if (!clickProps || !clickProps.steps) return;

        if (e.key === 'ArrowRight' && currentStep === getTourStep(steps, 'OkjY32s'))
          routeToDemoFermentation();

        if (
          e.key === 'ArrowRight' &&
          !step.disableNextButton &&
          currentStep !== getTourStep(steps, 'OkjY32s')
        ) {
          clickProps.setCurrentStep(
            Math.min(clickProps.currentStep + 1, clickProps.steps.length - 1),
          );
        }

        if (e.key === 'ArrowLeft' && !step.disablePrevButton) {
          clickProps.setCurrentStep(Math.max(clickProps.currentStep - 1, 0));
        }
        if (e.key === 'Escape') {
          clickProps.setIsOpen(false);
        }
      }}
    >
      {children}
    </TourProvider>
  );
}
